import React, { useEffect, useRef, useState } from "react";
import SearchIcon from "../../assets/SearchIcon";
import AngleRight from "../../assets/AngleRight";
import { SearchSubmitButton } from "../../assets/SearchSubmitButton";
import { PoweredByAIFindr } from "../../assets/PoweredByAIFindr";
import { PoweredByAIFindrResponse } from "../../assets/PoweredByAIFindrResponse";
import CloseIcon from "../../assets/CloseIcon";
import recommendations from "../../assets/recommendations.json";
import { useSearch } from "../../context/SearchContext";

interface SearchInputProps {
  isInternalSearch: boolean;
  isLoading?: boolean;
  currentSearchTerm?: string;
  queryDisplayMode: "hideSuggestedQueries" | "responsePage" | "searchPage";
  onSuggestionsEnabledChange?: (enabled: boolean) => void;
  onSearchSubmit?: () => void;
}

const SearchInput: React.FC<SearchInputProps> = ({
  isInternalSearch,
  isLoading = false,
  queryDisplayMode,
  onSuggestionsEnabledChange,
  onSearchSubmit,
}) => {
  const { handleSearch, currentSearchTerm } = useSearch();

  const [search, setSearch] = useState(currentSearchTerm || "");
  const [originalSearchTerm, setOriginalSearchTerm] = useState(
    currentSearchTerm || "",
  );
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestedQueriesRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const placeholderText = isInternalSearch
    ? "¿En qué más te puedo ayudar?"
    : "Buscar o seleccionar pregunta";

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const searchOrigin = isInternalSearch ? "internal" : "initial";
    handleSearch(search, searchOrigin, "keyword");
    setSuggestions([]);
    if (onSearchSubmit) onSearchSubmit();
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearch(suggestion);
    setSuggestions([]);
    const searchOrigin = isInternalSearch ? "internal" : "initial";
    handleSearch(suggestion, searchOrigin, "suggestion");
    if (onSearchSubmit) onSearchSubmit();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    if (value) {
      const exampleSuggestions = recommendations.labels;
      const normalizedValue = value
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
      setSuggestions(
        exampleSuggestions
          .filter((suggestion) => {
            const normalizedSuggestion = suggestion
              .normalize("NFD")
              .replace(/\p{Diacritic}/gu, "");
            return normalizedSuggestion
              .toLowerCase()
              .includes(normalizedValue.toLowerCase());
          })
          .slice(0, 6),
      );
    } else {
      setSuggestions([]);
    }
  };

  const clearSearch = () => {
    setSearch("");
    setSuggestions([]);
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (currentSearchTerm) {
      setSearch(currentSearchTerm);
      if (queryDisplayMode === "responsePage") {
        setOriginalSearchTerm(currentSearchTerm);
      }
    }
  }, [currentSearchTerm, queryDisplayMode]);

  useEffect(() => {
    if (onSuggestionsEnabledChange) {
      onSuggestionsEnabledChange(suggestions.length > 0);
    }
  }, [suggestions, onSuggestionsEnabledChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        queryDisplayMode === "responsePage" &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestedQueriesRef.current &&
        !suggestedQueriesRef.current.contains(event.target as Node)
      ) {
        // Handle click outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [queryDisplayMode]);

  const suggestionsEnabled = suggestions.length > 0;
  const shouldShowSubmitButton = () => {
    if (!search) return false;
    if (queryDisplayMode === "responsePage") {
      return search !== originalSearchTerm;
    }
    return true;
  };

  return (
    <div className="w-full px-0 mx-0 relative z-20">
      <form
        onSubmit={handleSubmit}
        className="relative w-full flex items-center justify-between px-0 mx-0"
      >
        <div
          id="searchBarRef"
          ref={searchBarRef}
          className="flex-grow w-full px-0 mx-0 md:relative"
        >
          <span className="w-full relative block">
            <div
              className={`w-full p-[3px] bg-white rounded-[32px] shadow-lg ${
                suggestionsEnabled
                  ? "md:shadow-[1px_-1px_4px_rgba(0,0,0,0.05)] md:rounded-t-3xl md:rounded-b-none"
                  : ""
              }`}
            >
              <input
                ref={inputRef}
                type="text"
                placeholder={placeholderText}
                value={search}
                onChange={handleInputChange}
                onFocus={() => {
                  inputRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit(e);
                  }
                }}
                className="w-full h-12 font-flexo rounded-[32px] pl-16 pr-10 p-2.5 text-BCP-dark-gray text-md bg-white outline-none md:text-ellipsis md:overflow-visible overflow-ellipsis whitespace-nowrap"
                disabled={isLoading}
              />
            </div>
            <span className="absolute inset-y-0 pt-2 left-1 flex items-center pl-4 pb-1">
              <SearchIcon className="w-8 h-8" color="#0a47f0" />
            </span>
            {search && (
              <span
                className="absolute inset-y-0 right-4 flex items-center cursor-pointer"
                onClick={clearSearch}
              >
                <CloseIcon className="w-6 h-6" />
              </span>
            )}
          </span>

          {suggestionsEnabled && (
            <div
              ref={suggestedQueriesRef}
              className="absolute z-30 inset-x-0 bottom-full mb-[5px] bg-white shadow-lg rounded-3xl md:inset-x-0 md:bottom-auto md:top-full md:mt-[-4px] md:mb-0 md:rounded-b-3xl md:rounded-t-none"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={`suggestion-${index}`}
                  className="flex-grow px-[3px] py-[0.8rem] w-full relative hover:bg-[#EBF4FF] group first:rounded-t-3xl last:rounded-b-3xl md:first:rounded-none"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <p className="font-flexo ml-16 mr-10 text-BCP-gray bg-transparent outline-none text-left group-hover:bg-[#EBF4FF] md:truncate md:max-w-full block min-h-[2.5rem] whitespace-normal leading-tight">
                    {(() => {
                      const searchLower =
                        search
                          ?.normalize("NFD")
                          .replace(/\p{Diacritic}/gu, "")
                          .toLowerCase() || "";
                      const suggestionLower = suggestion
                        .normalize("NFD")
                        .replace(/\p{Diacritic}/gu, "")
                        .toLowerCase();
                      const index = suggestionLower.indexOf(searchLower);

                      if (index === -1 || !search) {
                        return suggestion;
                      }

                      return (
                        <>
                          {suggestion.slice(0, index)}
                          <strong>
                            {suggestion.slice(index, index + search.length)}
                          </strong>
                          {suggestion.slice(index + search.length)}
                        </>
                      );
                    })()}
                  </p>
                  <span className="absolute inset-y-0 pt-1 left-1 flex items-center pl-4 pb-1">
                    <SearchIcon className="w-6 h-6" color="#4D5B70" />
                  </span>
                  <AngleRight className="absolute right-4 top-[1.1rem] w-5 h-5" />
                  {index !== suggestions.length - 1 && (
                    <div className="absolute bottom-0 left-5 right-5 h-[1px] bg-[#E5E7EB]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          className={`w-[54px] h-[54px] pr-[0.1rem] pt-[0.1rem] ml-3 flex-shrink-0 rounded-full bg-gradient-to-tr from-[#0026C9] to-[#E21732] ${
            shouldShowSubmitButton() ? "block" : "hidden"
          }`}
          type="submit"
          disabled={isLoading}
        >
          <SearchSubmitButton className="w-6 h-6 m-auto" />
        </button>
      </form>

      <div className="flex justify-center mt-2">
        {queryDisplayMode === "responsePage" ? (
          <div className="pl-20">
            <PoweredByAIFindrResponse width="124" height="14" />
          </div>
        ) : (
          <PoweredByAIFindr width="124" height="14" />
        )}
      </div>
    </div>
  );
};

export default SearchInput;
